import React from 'react';
import { MessageContentRenderer } from '../../components/MessageContent';
import { PreviewButton } from '../../components/MessageContent/PreviewButton';
import { DesignButton } from '../../components/MessageContent/DesignButton';
import type { ChatMessage } from './types';
import type { ContentBlock } from '../../types/contentBlock';

export interface MessageListProps {
  readonly messages: readonly ChatMessage[];
  readonly onPreview: (content: string, contentBlocks?: readonly ContentBlock[]) => void;
  /** 是否显示设计按钮 */
  readonly showDesignButton?: boolean;
  /** 点击设计按钮回调 */
  readonly onDesign?: (svgContent: string) => void;
  /** 插入代码到编辑器回调 */
  readonly onInsertCode?: (code: string, language: string) => void;
  /** 是否显示插入代码按钮 */
  readonly showInsertCodeBtn?: boolean;
  /** ★ 恢复数据回调：点击"恢复本次数据"按钮时触发 */
  readonly onRestore?: (version: string) => void;
}

const STATUS_TEXT_MAP: Record<string, string> = {
  thinking: '生成中',
  error: '调用失败',
  done: '完成'
};

/** ★ 从消息中提取 version 值 */
const extractVersion = (message: ChatMessage): string | null => {
  if (message.role !== 'assistant' || message.status !== 'done') return null;
  const version = message.workflowTrace?.outputs?.version;
  if (typeof version === 'string' && version.trim() !== '') return version.trim();
  if (typeof version === 'number') return String(version);
  return null;
};

const MessageItem: React.FC<{
  readonly message: ChatMessage;
  readonly onPreview: MessageListProps['onPreview'];
  readonly showDesignButton?: boolean;
  readonly onDesign?: (svgContent: string) => void;
  readonly onInsertCode?: (code: string, language: string) => void;
  readonly showInsertCodeBtn?: boolean;
  readonly onRestore?: (version: string) => void;
}> = ({
  message: m,
  onPreview,
  showDesignButton = false,
  onDesign,
  onInsertCode,
  showInsertCodeBtn = false,
  onRestore,
}) => {
  const isAssistantDone = m.role === 'assistant' && m.status === 'done';
  const isStreaming = m.status === 'thinking';
  const statusText = STATUS_TEXT_MAP[m.status ?? 'done'] ?? '完成';
  const safeContent = m.content ?? '';
  const safeDateCreated = m.dateCreated ?? new Date().toISOString();

  // ★ 提取 version
  const version = extractVersion(m);

  return (
    <div className={`message ${m.role === 'user' ? 'user' : 'bot'}`} data-message-id={m.id}>
      <div className="message-text-group">
        <div className="message-content">
          <MessageContentRenderer
            content={safeContent}
            isStreaming={isStreaming}
            thinkingItems={m.thinkingItems}
            files={m.files}
            workflowTrace={m.workflowTrace}
            contentBlocks={m.contentBlocks}
            onInsertCode={onInsertCode}
            showInsertCodeBtn={showInsertCodeBtn}
          />
        </div>
        <div className="message-footer">
          {isAssistantDone && (
            <PreviewButton
              content={safeContent}
              contentBlocks={m.contentBlocks}
              files={m.files}
              onClickPreview={() => onPreview(safeContent, m.contentBlocks)}
            />
          )}
          {isAssistantDone && showDesignButton && onDesign && (
            <DesignButton
              content={safeContent}
              onDesign={onDesign}
            />
          )}
          {/* ★ 恢复本次数据按钮：当 version 存在时显示 */}
          {version && onRestore && (
            <button
              type="button"
              className="msg-design-btn"
              onClick={() => onRestore(version)}
              title={`恢复数据到 ${version} 版本`}
            >
              <i className="fa fa-history" style={{ marginRight: 4 }} />
              恢复本次数据
            </button>
          )}
          <span className="timestamp">
            {m.role === 'user' ? '我' : '助手'} ·{' '}
            {new Date(safeDateCreated).toLocaleTimeString()}
            ({statusText})
          </span>
        </div>
      </div>
    </div>
  );
};

// ★ 解构 onRestore 并传递给 MessageItem
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  onPreview,
  showDesignButton,
  onDesign,
  onInsertCode,
  showInsertCodeBtn,
  onRestore,
}) => {
  return (
    <>
      {messages.map(m => (
        <MessageItem
          key={m.id}
          message={m}
          onPreview={onPreview}
          showDesignButton={showDesignButton}
          onDesign={onDesign}
          onInsertCode={onInsertCode}
          showInsertCodeBtn={showInsertCodeBtn}
          onRestore={onRestore}
        />
      ))}
    </>
  );
};